import { DateTime } from "luxon";
import type { AirportConfig, CounterFunction, FlightLeg, Turn } from "@yb/core";

export function hhmm(ms: number | null | undefined, tz: string): string {
  if (ms == null) return "—";
  return DateTime.fromMillis(ms, { zone: tz }).toFormat("HH:mm");
}

/**
 * An instant as the airport reads it, "3 Oct 14:05", in the airport's zone. A log read
 * from home, or on a laptop that never left Toronto time, would otherwise show every
 * entry hours out from the clock on the wall it is compared with.
 */
export function stamp(at: string | number, tz: string): string {
  const d = typeof at === "number" ? DateTime.fromMillis(at, { zone: tz }) : DateTime.fromISO(at, { zone: tz });
  return d.toFormat("d LLL HH:mm");
}

export function dayLabel(date: string, tz: string, today: string): string {
  if (date === today) return "Today";
  const d = DateTime.fromISO(date, { zone: tz });
  const t = DateTime.fromISO(today, { zone: tz });
  const diff = Math.round(d.diff(t, "days").days);
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  return d.toFormat("ccc d LLL");
}

export function longDate(date: string, tz: string): string {
  return DateTime.fromISO(date, { zone: tz }).toFormat("cccc d LLLL");
}

export function addDays(date: string, n: number): string {
  return DateTime.fromISO(date).plus({ days: n }).toISODate()!;
}

export function delayMin(leg: FlightLeg | null): number | null {
  if (!leg) return null;
  const t = leg.actual ?? leg.estimated;
  if (t == null) return null;
  return Math.round((t - leg.scheduled) / 60_000);
}

export function delayText(min: number | null): string {
  if (min === null || Math.abs(min) < 3) return "";
  return min > 0 ? `+${min}` : `${min}`;
}

export function label(t: Turn): string {
  const a = t.arr?.flightNumber;
  const d = t.dep?.flightNumber;
  if (a && d) return a === d ? a : `${a} › ${d}`;
  return (a ?? d)!;
}

export function airlineColor(code: string, config: AirportConfig | undefined): string {
  return config?.airlines[code]?.color ?? "#8a91a0";
}

/**
 * Black or white, whichever can be read on that colour. WestJet's teal and Flair's green
 * want opposite text, and a bar nobody can read is worse than an uncoloured one.
 */
export function readableOn(hex: string): string {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255) as [number, number, number];
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return luminance > 0.45 ? "#10131a" : "#ffffff";
}

export function airlineName(code: string, config: AirportConfig | undefined): string {
  return config?.airlines[code]?.name ?? code;
}

/** The second sentence of a tow toast: who the text reached, or why nobody. */
export function textedText(names: string[], airline: string): string {
  return names.length ? `Texted ${names.join(", ")}.` : `Nobody texted: no tow crew is set for ${airline}.`;
}

export function legStatusText(leg: FlightLeg): string {
  // The airport feed only carries about a day, so a flight that left it gets a worked-out status.
  const assumed = leg.statusAssumed ? " (no longer in the feed)" : "";
  return legStatusWord(leg) + assumed;
}

function legStatusWord(leg: FlightLeg): string {
  switch (leg.status) {
    case "landed":
      return "On block";
    case "departed":
      return "Departed";
    case "active":
      return leg.direction === "ARR" ? "Airborne" : "Departed";
    case "cancelled":
      return "Cancelled";
    case "diverted":
      return "Diverted";
    default:
      return leg.estimated && leg.estimated !== leg.scheduled ? "Estimated" : "Scheduled";
  }
}

export function pct(n: number): string {
  return `${Math.round(n)}%`;
}

/** Check-in counter uses, in the order a layout is usually written. */
export const COUNTER_FUNCTIONS: Array<{ id: CounterFunction; label: string; short: string }> = [
  { id: "priority", label: "Priority", short: "PRI" },
  { id: "checkin", label: "Check-in", short: "CHK" },
  { id: "bagdrop", label: "Bag drop", short: "BAG" },
  { id: "special", label: "Special / oversize", short: "SPL" },
];

export const counterFunctionLabel = (f: CounterFunction | null | undefined) => COUNTER_FUNCTIONS.find((x) => x.id === f)?.label ?? "";
export const counterFunctionShort = (f: CounterFunction | null | undefined) => COUNTER_FUNCTIONS.find((x) => x.id === f)?.short ?? "";
